import React from "react";
import { ClientProjectType } from "../types/ClientProjectType";
import { DefReactSelectOption } from "../types/DefReactSelectOption";
import { Pagination } from "../types/PagedResults";
import { useParams, useSearchParams } from "react-router-dom";
import { useLazyFetchMasterAccountProjectsListQuery } from "../services/clientprojects";

const ClientProjectContext = React.createContext<{ 
    clear: () => void,
    data: DefReactSelectOption<ClientProjectType>[],
    error: Error | string | null,
    hasMore: boolean,
    isFetching: boolean,
    isLoading: boolean,
    isNextPageLoading: boolean,
    isNewRecords: boolean,
    isRowLoaded: boolean,
    limit: number,
    loadMore: () => void,
    newRecordsSinceCache: number,
    page: number,
    pagination: Pagination,
    selectedValue: ClientProjectType | null,
    setPageLimit: (v: number) => void,
    setSearchTerm: (v: string) => void,
    setSelectedValue: (v: ClientProjectType | null) => void,
    sort: string,
    sortTable: (property: string) => void,
    totalCount: number,
    totalPages: number 
} | null>(null);

export const useClientProjectContext = () => React.useContext(ClientProjectContext);

export const ClientProjectProviderWithRouting = ({ children } : Readonly<{ children: JSX.Element }>) => {
    const { masterAccountId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [defaultProjectId, setDefaultProjectId] = React.useState<string | null>(null);

    React.useEffect(() => {
        let defaultProjectId = searchParams.get('projectId');
        setDefaultProjectId(defaultProjectId ?? null);
    }, [ searchParams ]);

    if (!masterAccountId) {
        return null;
    }

    return (
        <ClientProjectProvider masterAccountId={masterAccountId!} defaultProjectId={defaultProjectId}>
            {children}
        </ClientProjectProvider>
    );
}

export const ClientProjectProvider = ({ 
    children,
    defaultProjectId = null,
    filter = null,
    isReady = true,
    masterAccountId
} : Readonly<{
    children: JSX.Element,
    defaultProjectId?: string | null,
    filter?: any | null,
    isReady?: boolean,
    masterAccountId: string
}>) => {
    // setup state and our query state (does not load by default (lazy))
    const [sort, setSort] = React.useState<string>("CreatedDate+desc");
    const [pageLimit, setPageLimit] = React.useState<number>(50);
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [isClearing, setIsClearing] = React.useState<boolean>(false);
    const [isLoadingState, setIsLoadingState] = React.useState<boolean>(false);
    const [selectedValue, setSelectedValue] = React.useState<ClientProjectType | null>(null);
    const [load, { data, isLoading, isFetching, ...loadResults }] = useLazyFetchMasterAccountProjectsListQuery();

    // extract the results of the last load to our report search
    // note: we need to have a default set of data in-case we have an undefined response
    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isNextPageLoading = isFetching === true || isLoading === true || isLoadingState === true;

    const buildOptions = (cacheKey: number, page: number) : any => {
        // TODO: introduce filtering?
        return {
            cacheKey,
            masterAccountId,
            searchText: searchTerm,
            page: page,
            limit: pageLimit,
            sort
        }
    };

    const clear = () : void => {
        if (!masterAccountId || isNextPageLoading || isClearing || !isReady) {
            return;
        }

        const newCacheKey = new Date().valueOf();
        const queryOptions = buildOptions(newCacheKey, 1);

        setIsLoadingState(_ => true);
        setIsClearing(_ => true);
        load(queryOptions)
            .unwrap()
            .then(() => setCacheKey(newCacheKey))
            .finally(() => {
                setIsLoadingState(_prev => false);
                setIsClearing(_ => false);
            });
    };

    const isRowLoaded = (index: number) : boolean => {
        return !hasMore || index < results.length;
    }

    const loadMore = () : void => {
        if (isNextPageLoading || isClearing || !hasMore || !isReady) {
            return;
        }

        setIsLoadingState(_ => true);
        load(buildOptions(cacheKey, (page + 1))).unwrap().finally(() => {
            setIsLoadingState(_prev => false);
        });
    };

    const sortTable = (property : string) : void => {
        // we want the sort to initially be descending ??
        let newDirection : string | null = null;
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
    };

    React.useEffect(clear, [ searchTerm, sort, masterAccountId, filter, isReady ]);

    React.useEffect(() => {
        if (!defaultProjectId || !results) {
            setSelectedValue(null);
            return;
        }

        let selectedProject : DefReactSelectOption<ClientProjectType> | null = results.find((el: DefReactSelectOption<ClientProjectType>) => el.value === defaultProjectId);
        if (selectedProject) {
            setSelectedValue(selectedProject.data);
        }
    }, [ defaultProjectId, results ]);

    return <ClientProjectContext.Provider value={{
        ...loadResults,
        clear,
        data: isClearing ? [] : results,
        hasMore,
        isFetching,
        isLoading,
        isNextPageLoading,
        isNewRecords: newRecordsSinceCache > 0,
        isRowLoaded,
        limit: pageLimit,
        loadMore: isNextPageLoading ? () => {} : loadMore,
        newRecordsSinceCache,
        page,
        pagination,
        selectedValue,
        setPageLimit: (v) => setPageLimit(() => v),
        setSearchTerm: (v) => setSearchTerm(() => v),
        setSelectedValue: (v) => setSelectedValue(() => v),
        sort,
        sortTable,
        totalCount,
        totalPages
    }}>
        {children}
    </ClientProjectContext.Provider>
};